type SlugItem = { lang?: string; path?: string | null; published?: boolean | null };

const buildLocalizedPathFromTranslatedSlug = (slugPath: string, targetLang: string, fallbackCountry: string) => {
    const segments = slugPath.split('/').filter(Boolean);
    const firstSegment = segments[0] ?? '';
    const parsedLocale = parseLocaleFromPath(`/${firstSegment}`);

    const hasLocalePrefix = Boolean(parsedLocale?.country);
    const hasCountryPrefix = !hasLocalePrefix && Boolean(firstSegment);

    const country = parsedLocale?.country ?? (hasCountryPrefix ? firstSegment : fallbackCountry);
    const slugStartIndex = hasLocalePrefix || hasCountryPrefix ? 1 : 0;
    const slugWithoutPrefix = segments.slice(slugStartIndex).join('/');

    return slugWithoutPrefix ? `/${country}-${targetLang}/${slugWithoutPrefix}` : `/${country}-${targetLang}`;
};

export const useLocalizedRedirectOnLangSwitch = (translatedSlugs?: SlugItem[] | undefined | null) => {
    const route = useRoute();
    const router = useRouter();
    const { onLangSwitch } = useLangSwitchBus();

    let unsubscribe: (() => void) | undefined;

    if (import.meta.client) {
        unsubscribe = onLangSwitch(async (targetLang, targetCountry) => {
            try {
                if (!translatedSlugs?.length) return null;

                const targetSlug = translatedSlugs.find((slug) => slug.lang === targetLang && slug.published !== false);
                if (!targetSlug?.path) return null;

                // Use the requested target country as the fallback, not the current route's country,
                // otherwise a country switch can silently resolve back to the current country.
                const redirectPath = buildLocalizedPathFromTranslatedSlug(targetSlug.path, targetLang, targetCountry);
                const target = {
                    path: redirectPath,
                    query: route.query,
                    hash: route.hash
                };

                if (router.resolve(target).fullPath === router.currentRoute.value.fullPath) {
                    return null;
                }

                log.debug(
                    `[useLocalizedRedirectOnLangSwitch] Navigating to target path: ${target.path} with query: ${JSON.stringify(target.query)}`
                );
                await router.replace(target);
                return redirectPath;
            } catch {
                // Intentionally swallow navigation errors to prevent unhandled promise rejections
                return null;
            }
        });
    }

    onUnmounted(() => {
        unsubscribe?.();
    });
};
